// src/pages/ProductPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { client } from "../sanity/client";
import { urlFor } from "../sanity/image";

import Title from "../components/typography/Title";
import Subtitle from "../components/typography/Subtitle";
import Description from "../components/typography/Description";
import Button from "../shared/button/Button";

import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

type SanityImage = {
  alt?: string;
  asset?: {
    url?: string;
    metadata?: { dimensions?: { width?: number; height?: number } };
  };
};

type SanityFile = {
  asset?: {
    url?: string;
  };
};

type MediaBlock = {
  kind?: "image" | "videoFile" | "audioFile" | "embedUrl" | "gifUrl" | "icon";
  title?: string;
  caption?: string;
  alt?: string;

  image?: SanityImage | any;
  posterImage?: SanityImage | any;

  videoFile?: SanityFile | null;
  audioFile?: SanityFile | null;

  embedUrl?: string;
  gifUrl?: string;

  iconName?: string;

  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
};

type CTA = {
  label?: string;
  href?: string;
  variant?: "primary" | "secondary";
};

type ProductHero = {
  headline?: string;
  subheadline?: string;
  heroMedia?: MediaBlock[];
  primaryHeroMediaIndex?: number;
  ctas?: CTA[];
};

type FactsStripSection = {
  _type: "factsStrip";
  enabled?: boolean;
  anchor?: string;
  heading?: string;
  items?: { label?: string; value?: string; media?: MediaBlock }[];
};

type BenefitsGridSection = {
  _type: "benefitsGrid";
  enabled?: boolean;
  anchor?: string;
  heading?: string;
  subheading?: string;
  items?: { title?: string; description?: string; media?: MediaBlock }[];
};

type FeaturesGridSection = {
  _type: "featuresGrid";
  enabled?: boolean;
  anchor?: string;
  heading?: string;
  subheading?: string;
  items?: { title?: string; description?: string; media?: MediaBlock }[];
};
type HowItWorksParaSection = {
  _type: "howItWorksPara";
  enabled?: boolean;
  anchor?: string;
  heading?: string;
  content?: string;
};
type HowItWorksSection = {
  _type: "howItWorks";
  enabled?: boolean;
  anchor?: string;
  heading?: string;
  intro?: string;
  steps?: { title?: string; description?: string; media?: MediaBlock }[];
};

type MediaGallerySection = {
  _type: "mediaGallery";
  enabled?: boolean;
  anchor?: string;
  heading?: string;
  subheading?: string;
  media?: MediaBlock[];
};

type IntegrationsCoverageSection = {
  _type: "integrationsCoverage";
  enabled?: boolean;
  anchor?: string;
  heading?: string;
  variant?: "ats" | "languages" | "api" | "general";
  items?: {
    name?: string;
    category?: string;
    url?: string;
    description?: string;
    media?: MediaBlock;
  }[];
};

type TestimonialsSection = {
  _type: "productTestimonials";
  enabled?: boolean;
  anchor?: string;
  heading?: string;
  items?: {
    quote?: string;
    personName?: string;
    personTitle?: string;
    company?: string;
    media?: MediaBlock;
  }[];
};

type FAQSection = {
  _type: "productFaqs";
  enabled?: boolean;
  anchor?: string;
  heading?: string;
  items?: { question?: string; answer?: string; category?: string }[];
};

type BottomCTASection = {
  _type: "bottomCta";
  enabled?: boolean;
  anchor?: string;
  headline?: string;
  subheadline?: string;
  ctas?: CTA[];
  media?: MediaBlock;
};

type ProductSection =
  | FactsStripSection
  | BenefitsGridSection
  | FeaturesGridSection
  | HowItWorksSection
  | HowItWorksParaSection
  | MediaGallerySection
  | IntegrationsCoverageSection
  | TestimonialsSection
  | FAQSection
  | BottomCTASection;

type ProductDoc = {
  productName?: string;
  productKey?: string;
  order?: number;
  isListed?: boolean;

  hero?: ProductHero;
  sections?: ProductSection[];

  cardTitle?: string;
  cardDescription?: string;
  cardMedia?: MediaBlock;

  tabTitle?: string;
  seoTitle?: string;
  seoDescription?: string;
  focusKeyword?: string;
  metaKeywords?: string[];
  breadcrumbLabel?: string;
  seoImage?: SanityImage | null;
  seoImages?: SanityImage[] | null;
  canonical?: string;
  noIndex?: boolean;
  aiSummary?: string;

  aiSummaryLong?: string;
  llmTags?: string[];
  llmDifficulty?: string;
  llmEntities?: string[];
  llmQuestions?: string[];
  llmTakeaways?: string[];

  slug?: { current?: string };
};

const PRODUCT_BY_SLUG = `
*[_type == "productPage" && slug.current == $slug][0]{
  productName,
  productKey,
  order,
  isListed,

  hero{
    headline,
    subheadline,
    primaryHeroMediaIndex,
    heroMedia[]{
      kind,
      title,
      caption,
      alt,
      autoplay,
      loop,
      muted,
      embedUrl,
      gifUrl,
      iconName,
      image{
        alt,
        asset->{url, metadata{dimensions}}
      },
      posterImage{
        alt,
        asset->{url, metadata{dimensions}}
      },
      videoFile{
        asset->{url}
      },
      audioFile{
        asset->{url}
      }
    },
    ctas[]{
      label,
      href,
      variant
    }
  },

  sections[]{
    _type,
    enabled,
    anchor,
    heading,
    subheading,
    intro,
    variant,
    content,

    items[]{
      label,
      value,
      title,
      description,
      quote,
      personName,
      personTitle,
      company,
      question,
      answer,
      category,
      name,
      url,

      media{
        kind,
        title,
        caption,
        alt,
        autoplay,
        loop,
        muted,
        embedUrl,
        gifUrl,
        iconName,
        image{
          alt,
          asset->{url, metadata{dimensions}}
        },
        posterImage{
          alt,
          asset->{url, metadata{dimensions}}
        },
        videoFile{
          asset->{url}
        },
        audioFile{
          asset->{url}
        }
      }
    },

    steps[]{
      title,
      description,
      media{
        kind,
        title,
        caption,
        alt,
        autoplay,
        loop,
        muted,
        embedUrl,
        gifUrl,
        iconName,
        image{
          alt,
          asset->{url, metadata{dimensions}}
        },
        posterImage{
          alt,
          asset->{url, metadata{dimensions}}
        },
        videoFile{
          asset->{url}
        },
        audioFile{
          asset->{url}
        }
      }
    },

    media[]{
      kind,
      title,
      caption,
      alt,
      autoplay,
      loop,
      muted,
      embedUrl,
      gifUrl,
      iconName,
      image{
        alt,
        asset->{url, metadata{dimensions}}
      },
      posterImage{
        alt,
        asset->{url, metadata{dimensions}}
      },
      videoFile{
        asset->{url}
      },
      audioFile{
        asset->{url}
      }
    },

    headline,
    subheadline,
    ctas[]{
      label,
      href,
      variant
    },
    media{
      kind,
      title,
      caption,
      alt,
      autoplay,
      loop,
      muted,
      embedUrl,
      gifUrl,
      iconName,
      image{
        alt,
        asset->{url, metadata{dimensions}}
      },
      posterImage{
        alt,
        asset->{url, metadata{dimensions}}
      },
      videoFile{
        asset->{url}
      },
      audioFile{
        asset->{url}
      }
    }
  },

  cardTitle,
  cardDescription,
  cardMedia{
    kind,
    title,
    caption,
    alt,
    autoplay,
    loop,
    muted,
    embedUrl,
    gifUrl,
    iconName,
    image{
      alt,
      asset->{url, metadata{dimensions}}
    },
    posterImage{
      alt,
      asset->{url, metadata{dimensions}}
    },
    videoFile{
      asset->{url}
    },
    audioFile{
      asset->{url}
    }
  },

  tabTitle,
  seoTitle,
  seoDescription,
  focusKeyword,
  metaKeywords,
  breadcrumbLabel,
  seoImage{
    alt,
    asset->{url, metadata{dimensions}}
  },
  seoImages[]{
    alt,
    asset->{url, metadata{dimensions}}
  },
  canonical,
  noIndex,
  aiSummary,

  aiSummaryLong,
  llmTags,
  llmDifficulty,
  llmEntities,
  llmQuestions,
  llmTakeaways,

  slug
}
`;

// ---------- LAMBORGHINI SOLID ACCENTS (11 KEYS) ----------
type Accent = {
  pageBg: string;
  panelBg: string;
  accent: string;
  accA: string; // ✅ Add back gradient start
  accB: string; // ✅ Add back gradient end
  glow: string;
  border: string;
};

function getAccentByProductKey(productKey?: string): Accent {
  const key = (productKey || "").toLowerCase();

  const palette: Record<string, Accent> = {
    "ai-interviews": {
      pageBg: "#081B14",
      panelBg: "#0B241B",
      accent: "#2BFF88",
      accA: "#2BFF88", // ✅ Verde Mantis
      accB: "#1DD97D", // ✅ Slightly darker verde
      glow: "rgba(43,255,136,0.38)",
      border: "rgba(43,255,136,0.22)",
    },
    "ai-phone-screener": {
      pageBg: "#121027",
      panelBg: "#19163A",
      accent: "#C77DFF",
      accA: "#C77DFF", // ✅ Neon violet
      accB: "#9D4EDD", // ✅ Royal purple
      glow: "rgba(199,125,255,0.35)",
      border: "rgba(199,125,255,0.22)",
    },
    "jd-cv-matching": {
      pageBg: "#071C22",
      panelBg: "#0A2630",
      accent: "#00E5FF",
      accA: "#00E5FF", // ✅ Cyan supercar
      accB: "#00B4D8", // ✅ Deep cyan
      glow: "rgba(0,229,255,0.35)",
      border: "rgba(0,229,255,0.22)",
    },
    "coding-assessment": {
      pageBg: "#1A1508",
      panelBg: "#261E0B",
      accent: "#FDBB30",
      accA: "#FDBB30", // ✅ Golden yellow from gradient
      accB: "#D99A20", // ✅ Deeper gold
      glow: "rgba(253,187,48,0.35)",
      border: "rgba(253,187,48,0.22)",
    },
    "ai-sourcing": {
      pageBg: "#210A12",
      panelBg: "#2C0E19",
      accent: "#FF3D6E",
      accA: "#FF3D6E", // ✅ Rosso pink-hot
      accB: "#FF1744", // ✅ Racing red
      glow: "rgba(255,61,110,0.34)",
      border: "rgba(255,61,110,0.22)",
    },
    "ats-integrations": {
      pageBg: "#07121E",
      panelBg: "#0A1A2B",
      accent: "#4DA3FF",
      accA: "#4DA3FF", // ✅ Electric blue
      accB: "#2979FF", // ✅ Deep blue
      glow: "rgba(77,163,255,0.32)",
      border: "rgba(77,163,255,0.22)",
    },
    languages: {
      pageBg: "#140B1F",
      panelBg: "#1B102A",
      accent: "#B86BFF",
      accA: "#B86BFF", // ✅ Violet pop
      accB: "#9D4EDD", // ✅ Deep violet
      glow: "rgba(184,107,255,0.33)",
      border: "rgba(184,107,255,0.22)",
    },
    "ai-proctoring": {
      pageBg: "#1E0A0A",
      panelBg: "#281010",
      accent: "#FF2A2A",
      accA: "#FF2A2A", // ✅ Rosso Mars
      accB: "#D50000", // ✅ Deep racing red
      glow: "rgba(255,42,42,0.33)",
      border: "rgba(255,42,42,0.22)",
    },
    "whatsapp-dashboard": {
      pageBg: "#07170E",
      panelBg: "#0A2114",
      accent: "#25FF7A",
      accA: "#25FF7A", // ✅ Premium green
      accB: "#00E676", // ✅ Emerald
      glow: "rgba(37,255,122,0.32)",
      border: "rgba(37,255,122,0.22)",
    },
    dashboards: {
      pageBg: "#0A1220",
      panelBg: "#0F1A2E",
      accent: "#7AA7FF",
      accA: "#7AA7FF", // ✅ Cool blue
      accB: "#5E92FF", // ✅ Sky blue
      glow: "rgba(122,167,255,0.30)",
      border: "rgba(122,167,255,0.22)",
    },
    "ai-recruiter-in-a-box-api-platform": {
      pageBg: "#1A0F06",
      panelBg: "#241508",
      accent: "#FF6A00",
      accA: "#FF6A00", // ✅ Arancio Borealis
      accB: "#FF5722", // ✅ Deep orange
      glow: "rgba(255,106,0,0.34)",
      border: "rgba(255,106,0,0.22)",
    },
  };

  return (
    palette[key] || {
      pageBg: "#081B14",
      panelBg: "#0B241B",
      accent: "#2BFF88",
      accA: "#2BFF88",
      accB: "#1DD97D",
      glow: "rgba(43,255,136,0.38)",
      border: "rgba(43,255,136,0.22)",
    }
  );
}

// CSS vars at page root
function accentVars(a: Accent): React.CSSProperties {
  return {
    ["--pageBg" as any]: a.pageBg,
    ["--panelBg" as any]: a.panelBg,
    ["--acc" as any]: a.accent,
    ["--accA" as any]: a.accA, // ✅ Restore
    ["--accB" as any]: a.accB, // ✅ Restore
    ["--accGlow" as any]: a.glow,
    ["--accBorder" as any]: a.border,
  } as React.CSSProperties;
}
// Add these functions after accentVars()

// Solid Lamborghini-style hero backdrop
function solidHeroBg(): React.CSSProperties {
  return {
    backgroundColor: "var(--pageBg)",
    backgroundImage: `
      radial-gradient(900px 520px at 18% 28%, var(--accGlow), transparent 60%),
      radial-gradient(760px 420px at 82% 72%, rgba(255,255,255,0.07), transparent 62%)
    `,
  } as React.CSSProperties;
}

// Solid CTA strip
function solidCtaBg(): React.CSSProperties {
  return {
    backgroundColor: "var(--pageBg)",
    backgroundImage: `
      radial-gradient(1000px 560px at 20% 40%, var(--accGlow), transparent 62%),
      radial-gradient(820px 520px at 85% 65%, rgba(255,255,255,0.08), transparent 66%)
    `,
  } as React.CSSProperties;
}
function getMediaImageUrl(media: MediaBlock, width?: number): string | null {
  if (media.kind === "image" && (media as any)?.image?.asset?.url) {
    const base = (media as any).image.asset.url;
    if (width && urlFor) {
      try {
        return urlFor(base).width(width).url();
      } catch {
        return base;
      }
    }
    return base;
  }
  return null;
}

function MediaRender({
  media,
  className,
  style,
}: {
  media: MediaBlock;
  className?: string;
  style?: React.CSSProperties;
}) {
  if (media.kind === "image") {
    const url = getMediaImageUrl(media, 1200);
    if (!url) return null;
    return (
      <img
        src={url}
        alt={media.alt || media.title || ""}
        className={className || "h-auto w-full"}
        style={{ objectFit: "contain", ...style }}
        loading="lazy"
      />
    );
  }

  if (media.kind === "videoFile" && media.videoFile?.asset?.url) {
    const posterUrl = media.posterImage
      ? getMediaImageUrl(
          { ...media, kind: "image", image: media.posterImage },
          1200
        )
      : null;

    return (
      <video
        src={media.videoFile.asset.url}
        poster={posterUrl || undefined}
        controls={!media.autoplay}
        autoPlay={media.autoplay}
        loop={media.loop}
        muted={media.muted}
        className="h-auto w-full"
      />
    );
  }

  if (media.kind === "embedUrl" && media.embedUrl) {
    return (
      <div className="relative w-full pb-[56.25%]">
        <iframe
          src={media.embedUrl}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (media.kind === "gifUrl" && media.gifUrl) {
    return (
      <img
        src={media.gifUrl}
        alt={media.alt || media.title || ""}
        className="h-auto w-full"
        loading="lazy"
      />
    );
  }

  if (media.kind === "audioFile" && media.audioFile?.asset?.url) {
    return (
      <div
        style={{
          width: "100%",
          // padding: "1.5rem",
          // background: "rgba(255, 255, 255, 0.05)",
          // borderRadius: "12px",
          // border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <audio
          src={media.audioFile.asset.url}
          controls
          style={{
            width: "100%",
            height: "48px",
          }}
        >
          Your browser does not support the audio element.
        </audio>
        {media.title && (
          <div
            style={{
              marginTop: "0.75rem",
              fontSize: "0.85rem",
              color: "rgba(255, 255, 255, 0.7)",
              textAlign: "center",
            }}
          >
            {media.title}
          </div>
        )}
      </div>
    );
  }

  return null;
}

function anchorId(sec: { anchor?: string }): string {
  return sec.anchor || "";
}

export default function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<ProductDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const [activeHeroMediaIndex, setActiveHeroMediaIndex] = useState(0);

  useEffect(() => {
    if (!slug) return;

    client
      .fetch<ProductDoc>(PRODUCT_BY_SLUG, { slug })
      .then((data) => {
        setProduct(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [slug]);

  // ✅ ACCENT PER PRODUCT KEY (ONLY COLOR SYSTEM)
  const accentKey = useMemo(() => {
    return (
      product?.productKey ||
      product?.slug?.current ||
      slug ||
      ""
    ).toLowerCase();
  }, [product?.productKey, product?.slug?.current, slug]);

  const accent = useMemo(() => getAccentByProductKey(accentKey), [accentKey]);

  const llmHtml = useMemo(() => {
    if (!product) return "";
    const parts: string[] = [];

    if (product.aiSummary) parts.push(`<p>${product.aiSummary}</p>`);
    if (product.aiSummaryLong) parts.push(`<p>${product.aiSummaryLong}</p>`);

    if (product.llmTags && product.llmTags.length > 0) {
      parts.push(`<p>Tags: ${product.llmTags.join(", ")}</p>`);
    }
    if (product.llmDifficulty) {
      parts.push(`<p>Difficulty: ${product.llmDifficulty}</p>`);
    }
    if (product.llmEntities && product.llmEntities.length > 0) {
      parts.push(`<p>Entities: ${product.llmEntities.join(", ")}</p>`);
    }
    if (product.llmQuestions && product.llmQuestions.length > 0) {
      parts.push(`<p>Questions: ${product.llmQuestions.join(" | ")}</p>`);
    }
    if (product.llmTakeaways && product.llmTakeaways.length > 0) {
      parts.push(`<p>Takeaways: ${product.llmTakeaways.join(" | ")}</p>`);
    }

    return parts.join("\n");
  }, [product]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "var(--paper)",
        }}
      >
        <div style={{ color: "var(--ink)", fontSize: "1.25rem" }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "var(--paper)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              color: "var(--ink)",
              marginBottom: "1rem",
            }}
          >
            Product Not Found
          </h1>
          <Link
            to="/"
            style={{ color: "var(--accent)", textDecoration: "none" }}
          >
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  const seoTitle = product.seoTitle || product.productName || "Product";
  const seoDesc = product.seoDescription || "";
  const canonicalUrl = product.canonical || "";
  const shouldNoIndex = product.noIndex || false;

  const seoImageUrl =
    product.seoImage?.asset?.url ||
    product.seoImages?.[0]?.asset?.url ||
    product.cardMedia
      ? getMediaImageUrl(product.cardMedia, 1200)
      : null;

  const hero = product.hero || {};
  const sections = (product.sections || []).filter((s) => s.enabled !== false);

  const primaryMediaIndex = hero.primaryHeroMediaIndex ?? 0;
  const heroMediaArray = hero.heroMedia || [];
  const primaryMedia =
    heroMediaArray.length > 0 ? heroMediaArray[primaryMediaIndex] : null;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        {seoDesc && <meta name="description" content={seoDesc} />}
        {product.focusKeyword && (
          <meta name="keywords" content={product.focusKeyword} />
        )}
        {product.metaKeywords && product.metaKeywords.length > 0 && (
          <meta name="keywords" content={product.metaKeywords.join(", ")} />
        )}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {shouldNoIndex && <meta name="robots" content="noindex,nofollow" />}

        <meta property="og:title" content={seoTitle} />
        {seoDesc && <meta property="og:description" content={seoDesc} />}
        {seoImageUrl && <meta property="og:image" content={seoImageUrl} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        {seoDesc && <meta name="twitter:description" content={seoDesc} />}
        {seoImageUrl && <meta name="twitter:image" content={seoImageUrl} />}
      </Helmet>

      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        
        :root {
          --ink: #ffffff;
          --paper: #0a1410;
          --cream: #d4ede5;
          --accent: #10b981;
          --accent-light: #34d399;
          --muted: rgba(255,255,255,0.7);
          --card-bg: rgba(255,255,255,0.05);
          --card-ink: #ffffff;
          --card-muted: rgba(255,255,255,0.6);
          --card-border: rgba(255,255,255,0.1);
          --border: rgba(255,255,255,0.1);
        }
        
        html { scroll-behavior: smooth; }
        
        .features-section {
          padding: 2rem 5rem 8rem;
        }

        .features-grid {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          grid-template-rows: auto auto;
          gap: 1.5rem;
        }

        .feat-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 18px;
          padding: 2.2rem;
          transition: all 0.3s ease;
        }

        .feat-card:hover {
          border-color: var(--acc);
          background: rgba(255, 255, 255, 0.05);
          box-shadow: 0 8px 32px var(--accGlow);
          transform: translateY(-2px);
        }

        .feat-card.tall {
          grid-row: span 2;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .feat-icon {
          width: 44px;
          height: 44px;
          background: var(--cream);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          margin-bottom: 1.25rem;
        }

        

        .feat-desc {
          font-size: 0.9rem;
          color: var(--muted);
          line-height: 1.6;
          font-weight: 300;
        }

        .feat-image-wrapper {
          margin-top: 1.5rem;
          border-radius: 12px;
          overflow: hidden;
          width: 100%;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .feat-card.tall .feat-image-wrapper {
          margin-top: auto;
          min-height: 200px;
        }

        .feat-image {
          width: 100%;
          height: auto;
          display: block;
          object-fit: cover;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .card-hover {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .card-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(59, 130, 246, 0.15);
        }
        
        .timeline {
          position: relative;
          padding-left: 2.5rem;
        }
        
        .timeline::before {
          content: '';
          position: absolute;
          left: 11px;
          top: 0;
          bottom: 0;
          width: 2px;
background: var(--acc);        }
        
        .timeline-step {
          position: relative;
          margin-bottom: 2.8rem;
        }
        
        .timeline-step:last-child {
          margin-bottom: 0;
        }
        
        .timeline-dot {
          position: absolute;
          left: -2.5rem;
          top: 0.35rem;
          width: 24px;
          height: 24px;
          background: var(--pageBg);
          border: 3px solid var(--acc);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 0 3px var(--pageBg);
        }
        
        .timeline-dot-inner {
          width: 8px;
          height: 8px;
          background: var(--acc);
          border-radius: 50%;
        }
        
        .timeline-step-number {
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--acc);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 0.25rem;
        }
        
       
        
        .timeline-step-desc {
          font-size: 0.85rem;
          color: var(--muted);
          line-height: 1.6;
          max-width: 380px;
        }
        
        /* Hero Carousel Dots Styling */
        .slick-dots li button:before {
          display: none;
        }
        
        .slick-dots li.slick-active div {
          background: var(--acc) !important;
          width: 10px !important;
          height: 10px !important;
        }
        
        .testimonial-nav {
          background: none;
          border: 1px solid var(--card-border);
          border-radius: 999px;
          padding: 0.4rem 0.9rem;
          font-size: 0.7rem;
          color: var(--card-ink);
          cursor: pointer;
        }

        .testimonial-nav:hover {
          border-color: var(--acc);
        }

        .hero-carousel-container {
          flex-direction: row;
        }

        @media (max-width: 768px) {
          .hero-carousel-container {
            flex-direction: column;
          }
          .hero-carousel-container > div:last-child {
            width: 100% !important;
            flex-direction: row !important;
            overflow-x: auto;
            gap: 0.75rem;
          }
          
          /* Benefits Grid Responsive */
          .benefits-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
          }
        }
      `}</style>

      {/* ✅ LAMBORGHINI SOLID THEME VARS */}
      <main
        style={{
          ...accentVars(accent),
          minHeight: "100vh",
          background: "var(--pageBg)",
        }}
      >
        {/* HERO */}
        <section className="relative w-screen overflow-hidden px-4 py-12 sm:px-6 md:px-10">
          {/* ✅ Boomerang gradient background (ONLY IN HERO) */}
          <div className="absolute inset-0" style={solidHeroBg()} />
          <div className="absolute inset-0 bg-black/10" />

          <div className="relative z-20 mx-auto w-full max-w-6xl">
            {/* Text Content */}
            <div className="mx-auto max-w-4xl text-center">
              {hero.headline && (
                <Title as="h1" size="xl" className="mt-3 text-white">
                  {hero.headline}
                </Title>
              )}

              {hero.subheadline && (
                <Description className="mx-auto mt-4 mb-4 max-w-3xl text-white/80">
                  {hero.subheadline}
                </Description>
              )}
            </div>

            {/* Media Carousel - keep your existing logic */}
            {heroMediaArray && heroMediaArray.length > 0 && (
              <div
                style={{
                  width: "100%",
                  maxWidth: "1200px",
                  marginBottom: "3rem",
                  display: "flex",
                  gap: "1.5rem",
                  alignItems: "flex-start",
                }}
                className="hero-carousel-container"
              >
                {heroMediaArray.length === 1 ? (
                  <div
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "20px",
                      padding: "2.5rem",
                      width: "100%",
                    }}
                  >
                    <MediaRender media={heroMediaArray[0]} />
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        flex: 1,
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "1.5rem",
                          left: "1.5rem",
                          zIndex: 10,
                          fontSize: "0.85rem",
                          color: "rgba(255,255,255,0.8)",
                          fontWeight: 500,
                          background: "rgba(0,0,0,0.3)",
                          backdropFilter: "blur(10px)",
                          padding: "0.4rem 0.9rem",
                          borderRadius: "8px",
                          border: `1px solid var(--accBorder)`,
                        }}
                      >
                        Preview {activeHeroMediaIndex + 1}/
                        {heroMediaArray.length}
                      </div>

                      <div
                        style={{
                          background: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: "20px",
                          padding: "2.5rem",
                          width: "100%",
                          aspectRatio: "16/10", // Fixed aspect ratio for consistency
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <MediaRender
                          media={heroMediaArray[activeHeroMediaIndex]}
                          className="h-full w-full"
                          style={{
                            objectFit: "contain",
                            maxHeight: "100%",
                            maxWidth: "100%",
                          }}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        width: "200px",
                        flexShrink: 0,
                      }}
                    >
                      {heroMediaArray.map((media, idx) => {
                        const isActive = idx === activeHeroMediaIndex;
                        return (
                          <button
                            key={idx}
                            onClick={() => setActiveHeroMediaIndex(idx)}
                            style={{
                              background: isActive
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(255,255,255,0.03)",
                              border: isActive
                                ? "2px solid rgba(255,255,255,0.3)"
                                : "1px solid rgba(255,255,255,0.1)",
                              borderRadius: "16px",
                              padding: "1rem",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                              overflow: "hidden",
                              position: "relative",
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.background =
                                  "rgba(255,255,255,0.05)";
                                e.currentTarget.style.borderColor =
                                  "rgba(255,255,255,0.2)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.background =
                                  "rgba(255,255,255,0.03)";
                                e.currentTarget.style.borderColor =
                                  "rgba(255,255,255,0.1)";
                              }
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                aspectRatio: "16/10",
                                borderRadius: "8px",
                                overflow: "hidden",
                                background: "rgba(0,0,0,0.2)",
                              }}
                            >
                              <MediaRender
                                media={media}
                                className="h-full w-full"
                                style={{ objectFit: "cover" }}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* CTAs with solid accent buttons */}
            {hero.ctas && hero.ctas.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                {hero.ctas.slice(0, 2).map((cta, idx) => {
                  const isPrimary = idx === 0;
                  return (
                    <a key={idx} href={cta.href || "#"}>
                      <button
                        className={[
                          "rounded-xl px-7 py-3 text-base shadow-none transition",
                          isPrimary
                            ? "text-black hover:brightness-110"
                            : "border bg-transparent text-white hover:bg-white/10",
                        ].join(" ")}
                        style={
                          isPrimary
                            ? {
                                backgroundColor: "var(--acc)",
                                boxShadow: "0 0 0 1px rgba(0,0,0,0.25)",
                              }
                            : { borderColor: "rgba(255,255,255,0.28)" }
                        }
                      >
                        {cta.label ||
                          (isPrimary ? "Get Started" : "Learn More")}
                      </button>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </section>
        {/* Sections */}
        <div className="mx-auto w-full max-w-6xl space-y-14 px-4 pb-16 pt-6 sm:px-6 md:px-10">
          {sections.map((section, idx) => {
            // Facts Strip (Metrics Strip)
            if (section._type === "factsStrip") {
              const sec = section as FactsStripSection;
              if (!sec.items || sec.items.length === 0) return null;

              return (
                <section key={idx} id={anchorId(sec)} style={{ margin: "0" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: "1px",
                      background: "var(--card-border)",
                    }}
                    className="sm:grid-cols-2 lg:grid-cols-4"
                  >
                    {sec.items.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          background: "var(--card-bg)",
                          padding: "2.5rem 2rem",
                          textAlign: "center",
                        }}
                      >
                        {item.value && (
                          <div
                            style={{
                              fontSize: "2.8rem",
                              letterSpacing: "-0.03em",
                              // color: "var(--card-ink)",
                              lineHeight: 1,
                              marginBottom: "0.4rem",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: item.value.replace(
                                /([+–—\-\/×])/g,
                                '<span style="color: var(--ink)">$1</span>'
                              ),
                            }}
                          />
                        )}
                        {item.label && (
                          <div
                            style={{
                              fontSize: "0.78rem",
                              color: "var(--card-muted)",
                              fontWeight: 400,
                            }}
                          >
                            {item.label}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              );
            }

            // How It Works - Timeline Style
            if (section._type === "howItWorks") {
              const sec = section as HowItWorksSection;
              if (!sec.steps || sec.steps.length === 0) return null;

              return (
                <section
                  key={idx}
                  id={anchorId(sec)}
                  style={{ padding: "8rem 0" }}
                >
                  {/* Centered Header */}
                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: "4rem",
                      maxWidth: "800px",
                      margin: "0 auto 4rem",
                    }}
                  >
                    {sec.heading && (
                      <h2
                        className="section-title font-serif"
                        style={{
                          fontSize: "clamp(2.2rem, 3.5vw, 3rem)",
                          letterSpacing: "-0.02em",
                          lineHeight: 1.15,
                          color: "#ffffff",
                          marginBottom: "1rem",
                        }}
                      >
                        {sec.heading}
                      </h2>
                    )}
                    {sec.intro && (
                      <p
                        className="section-desc"
                        style={{
                          fontSize: "0.95rem",
                          color: "rgba(255,255,255,0.7)",
                          lineHeight: 1.7,
                          fontWeight: 300,
                        }}
                      >
                        {sec.intro}
                      </p>
                    )}
                  </div>

                  <div
                    style={{
                      position: "relative",
                      paddingLeft: "2.5rem",
                    }}
                  >
                    {/* Timeline Line */}
                    <div
                      style={{
                        content: "",
                        position: "absolute",
                        left: "11px",
                        top: 0,
                        bottom: 0,
                        width: "2px",
                        background: `linear-gradient(to bottom, var(--acc), var(--accBorder))`,
                      }}
                    />

                    {sec.steps.map((step, i) => (
                      <div
                        key={i}
                        style={{
                          position: "relative",
                          marginBottom:
                            i === sec.steps!.length - 1 ? 0 : "2.8rem",
                        }}
                      >
                        {/* Timeline Dot */}
                        <div
                          style={{
                            position: "absolute",
                            left: "-2.5rem",
                            top: "0.35rem",
                            width: "24px",
                            height: "24px",
                            background: "var(--pageBg)",
                            border: `3px solid var(--acc)`,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: `0 0 0 3px var(--pageBg)`,
                            zIndex: 1,
                          }}
                        >
                          <div
                            style={{
                              width: "8px",
                              height: "8px",
                              background: "var(--acc)",
                              borderRadius: "50%",
                            }}
                          />
                        </div>

                        {/* Step Number */}
                        <div
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "var(--acc)",
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            marginBottom: "0.25rem",
                          }}
                        >
                          Step {String(i + 1).padStart(2, "0")}
                        </div>
                        {/* Step Title */}
                        {step.title && (
                          <h3
                            className="font-serif"
                            style={{
                              fontSize: "1.45rem",
                              letterSpacing: "-0.02em",
                              marginBottom: "0.35rem",
                              color: "#ffffff",
                            }}
                          >
                            {step.title}
                          </h3>
                        )}

                        {/* Step Description */}
                        {step.description && (
                          <p
                            style={{
                              fontSize: "0.85rem",
                              color: "rgba(255,255,255,0.7)",
                              lineHeight: 1.6,
                              maxWidth: "380px",
                            }}
                          >
                            {step.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              );
            }

            // How It Works Paragraph - Simple Text Section
if (section._type === "howItWorksPara") {
  const sec = section as HowItWorksParaSection;
  
  return (
    <section
      key={idx}
      id={anchorId(sec)}
      className="mx-auto w-full max-w-4xl"
      style={{ padding: "4rem 0" }}
    >
      {sec.heading && (
        <h2
          className="font-serif"
          style={{
            fontSize: "clamp(2rem, 3vw, 2.5rem)",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            color: "#ffffff",
            marginBottom: "1.5rem",
            textAlign: "center",
          }}
        >
          {sec.heading}
        </h2>
      )}
      
      {sec.content && (
        <p
          style={{
            fontSize: "1.05rem",
            color: "rgba(255,255,255,0.8)",
            lineHeight: 1.8,
            fontWeight: 300,
            textAlign: "center",
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          {sec.content}
        </p>
      )}
    </section>
  );
}

            // Benefits Grid - Conditional Layout Based on Product
            if (section._type === "benefitsGrid") {
              const sec = section as BenefitsGridSection;
              const items = sec.items || [];
              if (items.length === 0) return null;

              // Check if this is JD-CV Matching page (or other products that need compact layout)
              const isCompactProduct = [
                "jd-cv-matching",
                "coding-assessment","ats-integrations",
                "whatsapp-dashboard"
              ].includes(slug?.toLowerCase() || "");

              return (
                <section
                  key={idx}
                  id={anchorId(sec)}
                  className="mx-auto w-full max-w-6xl rounded-[28px] border p-6 sm:p-10"
                  style={{
                    backgroundColor: "var(--panelBg)",
                    borderColor: "rgba(255,255,255,0.10)",
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.35)",
                  }}
                >
                  {sec.heading && (
                    <Title as="h2" size="lg" className="text-white mb-4">
                      {sec.heading}
                    </Title>
                  )}

                  {isCompactProduct ? (
                    // COMPACT LAYOUT - Premium Modern Design
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(340px, 1fr))",
                        gap: "1.25rem",
                        maxWidth: "1200px",
                        margin: "0 auto",
                      }}
                    >
                      {items.map((benefit, i) => (
                        <div
                          key={i}
                          style={{
                            position: "relative",
                            background: "var(--panelBg)",
                            border: "1px solid var(--card-border)",
                            borderRadius: "20px",
                            padding: "2rem",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            overflow: "hidden",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--accBorder)";
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 12px 40px var(--accGlow)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--card-border)";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          {/* Gradient Bar Top */}
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "4px",
                              background: `var(--acc)`,
                            }}
                          />

                          {/* Number + Title Row */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "1rem",
                              marginBottom: "1rem",
                            }}
                          >
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                minWidth: "32px",
                                borderRadius: "8px",
                                background: `var(--acc)`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.9rem",
                                fontWeight: 700,
                                color: "#ffffff",
                                boxShadow: `0 4px 12px var(--accGlow)`,
                              }}
                            >
                              {String(i + 1).padStart(2, "0")}
                            </div>

                            {benefit.title && (
                              <h3
                                className="font-serif"
                                style={{
                                  fontSize: "1.3rem",
                                  letterSpacing: "-0.02em",
                                  color: "var(--ink)",
                                  lineHeight: 1.25,
                                  margin: 0,
                                  flex: 1,
                                }}
                              >
                                {benefit.title}
                              </h3>
                            )}
                          </div>

                          {/* Description */}
                          {benefit.description && (
                            <p
                              style={{
                                fontSize: "0.88rem",
                                color: "var(--muted)",
                                lineHeight: 1.65,
                                fontWeight: 300,
                                margin: 0,
                              }}
                            >
                              {benefit.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    // STANDARD LAYOUT for all other products
                    <div
                      className="benefits-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: "2rem",
                      }}
                    >
                      {items.map((benefit, i) => (
                        <article
                          key={i}
                          style={{
                            background: "var(--panelBg)",
                            border: "1px solid var(--card-border)",
                            borderRadius: "20px",
                            padding: "2.5rem",
                            transition: "all 0.3s ease",
                            position: "relative",
                            overflow: "hidden",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--accBorder)";
                            e.currentTarget.style.background =
                              "rgba(255, 255, 255, 0.05)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 32px var(--accGlow)";
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--card-border)";
                            e.currentTarget.style.background = "var(--panelBg)";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          {/* Title */}
                          {benefit.title && (
                            <h3
                              className="font-serif"
                              style={{
                                fontSize: "1.5rem",
                                letterSpacing: "-0.02em",
                                marginBottom: "1rem",
                                color: "var(--ink)",
                                lineHeight: 1.3,
                              }}
                            >
                              {benefit.title}
                            </h3>
                          )}

                          {/* Description */}
                          {benefit.description && (
                            <p
                              style={{
                                fontSize: "0.9rem",
                                color: "var(--muted)",
                                lineHeight: 1.7,
                                fontWeight: 300,
                                marginBottom: "1.5rem",
                              }}
                            >
                              {benefit.description}
                            </p>
                          )}

                          {/* Media */}
                          {benefit.media && (
                            <div
                              style={{
                                marginTop: "1.5rem",
                                borderRadius: "12px",
                                overflow: "hidden",
                                // border: "1px solid rgba(255, 255, 255, 0.05)",
                              }}
                            >
                              {benefit.media.kind === "audioFile" ? (
                                <MediaRender media={benefit.media} />
                              ) : (
                                <div style={{ aspectRatio: "16/10" }}>
                                  <MediaRender
                                    media={benefit.media}
                                    className="h-full w-full"
                                    style={{ objectFit: "contain" }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              );
            }

         // Features Grid - Alternating Showcase Layout
if (section._type === "featuresGrid") {
  const sec = section as FeaturesGridSection;
  if (!sec.items || sec.items.length === 0) return null;

  return (
    <section
      key={idx}
      id={anchorId(sec)}
      style={{ padding: "8rem 0" }}
    >
      {/* Header Section */}
      <div style={{ textAlign: "center", marginBottom: "5rem" }}>
        {sec.heading && (
          <h2
            className="font-serif"
            style={{
              fontSize: "clamp(2.2rem, 3.5vw, 3rem)",
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              color: "#ffffff",
              marginBottom: "1rem",
            }}
          >
            {sec.heading}
          </h2>
        )}
        {sec.subheading && (
          <p
            style={{
              fontSize: "1rem",
              color: "rgba(255,255,255,0.7)",
              lineHeight: 1.7,
              fontWeight: 300,
              maxWidth: "700px",
              margin: "0 auto",
            }}
          >
            {sec.subheading}
          </p>
        )}
      </div>

      {/* Features - Alternating Layout */}
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {sec.items.map((item, i) => {
          const isEven = i % 2 === 0;
          const hasMedia = item.media;
          
          // Text-only feature (compact card)
          if (!hasMedia) {
            return (
              <div
                key={i}
                style={{
                  marginBottom: i === sec.items.length - 1 ? "0" : "2rem",
                  padding: "2rem 2.5rem",
                  background: "rgba(255, 255, 255, 0.02)",
                  border: "1px solid rgba(255, 255, 255, 0.06)",
                  borderRadius: "20px",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                  e.currentTarget.style.borderColor = "var(--accBorder)";
                  e.currentTarget.style.boxShadow = "0 8px 40px var(--accGlow)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.06)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Feature Number */}
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "12px",
                      background: `linear-gradient(135deg, var(--accA), var(--accB))`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      color: "#000000",
                      boxShadow: `0 4px 20px var(--accGlow)`,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  {/* <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "var(--acc)",
                      textTransform: "uppercase",
                      letterSpacing: "0.15em",
                    }}
                  >
                    Feature {String(i + 1).padStart(2, "0")}
                  </div> */}
                </div>

                {/* Title */}
                {item.title && (
                  <h3
                    className="font-serif"
                    style={{
                      fontSize: "clamp(1.3rem, 1.8vw, 1.6rem)",
                      letterSpacing: "-0.02em",
                      color: "#ffffff",
                      lineHeight: 1.25,
                      marginBottom: "0.75rem",
                    }}
                  >
                    {item.title}
                  </h3>
                )}

                {/* Description */}
                {item.description && (
                  <p
                    style={{
                      fontSize: "0.9rem",
                      color: "rgba(255,255,255,0.75)",
                      lineHeight: 1.8,
                      fontWeight: 300,
                    }}
                  >
                    {item.description}
                  </p>
                )}
              </div>
            );
          }
          
          // Feature with media (showcase card)
          return (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "3rem",
                alignItems: "center",
                marginBottom: i === sec.items.length - 1 ? "0" : "3rem",
                padding: "3rem",
                background: "rgba(255, 255, 255, 0.02)",
                border: "1px solid rgba(255, 255, 255, 0.06)",
                borderRadius: "28px",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                e.currentTarget.style.borderColor = "var(--accBorder)";
                e.currentTarget.style.boxShadow = "0 8px 40px var(--accGlow)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.02)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.06)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {/* Content Side */}
              <div style={{ order: !isEven ? 2 : 1 }}>
                {/* Feature Number */}
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    marginBottom: "1.5rem",
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "14px",
                      background: `linear-gradient(135deg, var(--accA), var(--accB))`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#000000",
                      boxShadow: `0 4px 20px var(--accGlow)`,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  {/* <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "var(--acc)",
                      textTransform: "uppercase",
                      letterSpacing: "0.15em",
                    }}
                  >
                    Feature {String(i + 1).padStart(2, "0")}
                  </div> */}
                </div>

                {/* Title */}
                {item.title && (
                  <h3
                    className="font-serif"
                    style={{
                      fontSize: "clamp(1.6rem, 2.2vw, 2rem)",
                      letterSpacing: "-0.02em",
                      color: "#ffffff",
                      lineHeight: 1.25,
                      marginBottom: "1.25rem",
                    }}
                  >
                    {item.title}
                  </h3>
                )}

                {/* Description */}
                {item.description && (
                  <p
                    style={{
                      fontSize: "1rem",
                      color: "rgba(255,255,255,0.75)",
                      lineHeight: 1.8,
                      fontWeight: 300,
                    }}
                  >
                    {item.description}
                  </p>
                )}
              </div>

              {/* Media Side */}
              <div
                style={{
                  order: !isEven ? 1 : 2,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    background: "rgba(0, 0, 0, 0.3)",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
                  }}
                >
                  {item.media.kind === "audioFile" ? (
                    <MediaRender media={item.media} />
                  ) : (
                    <div style={{ aspectRatio: "16/10", width: "100%" }}>
                      <MediaRender
                        media={item.media}
                        className="h-full w-full"
                        style={{ objectFit: "cover" }}
                      />
                    </div>
                  )}
                </div>
                
                {/* Decorative Glow */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "80%",
                    height: "80%",
                    background: `radial-gradient(circle, var(--accGlow), transparent 70%)`,
                    filter: "blur(40px)",
                    opacity: 0.3,
                    zIndex: -1,
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
            // Testimonials
            if (section._type === "productTestimonials") {
              const sec = section as TestimonialsSection;
              if (!sec.items || sec.items.length === 0) return null;

              const activeTestimonial =
                sec.items[activeTestimonialIndex] || sec.items[0];

              return (
                <section
                  key={idx}
                  id={anchorId(sec)}
                  style={{ padding: "6rem 0" }}
                >
                  {/* Heading Section */}
                  {sec.heading && (
                    <Title as="h2" size="lg" className="mb-4">
                      {sec.heading}
                    </Title>
                  )}

                  <div
                    className="group relative overflow-hidden rounded-3xl border border-gray-200 bg-white p-8 transition duration-300 hover:shadow-xl"
                    style={{
                      padding: "3.5rem",
                    }}
                  >
                    {/* Quote section */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "1.5rem",
                        marginBottom: "2.8rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "5rem",
                          lineHeight: 0.8,
                          color: "#e5e7eb",
                          flexShrink: 0,
                          marginTop: "0.2rem",
                        }}
                      >
                        "
                      </div>

                      <div style={{ flex: 1 }}>
                        {activeTestimonial.quote && (
                          <p
                            style={{
                              fontSize: "clamp(1.5rem, 2.2vw, 1.9rem)",
                              lineHeight: 1.4,
                              letterSpacing: "-0.02em",
                              color: "#111827",
                              fontStyle: "italic",
                            }}
                          >
                            {activeTestimonial.quote}
                          </p>
                        )}

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            marginTop: "1.5rem",
                          }}
                        >
                          {activeTestimonial.media && (
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                background: "#f3f4f6",
                                border: "1px solid #e5e7eb",
                                overflow: "hidden",
                              }}
                            >
                              <MediaRender media={activeTestimonial.media} />
                            </div>
                          )}

                          <div>
                            {activeTestimonial.personName && (
                              <div
                                style={{
                                  fontSize: "0.82rem",
                                  fontWeight: 500,
                                  color: "#111827",
                                }}
                              >
                                {activeTestimonial.personName}
                              </div>
                            )}

                            {(activeTestimonial.personTitle ||
                              activeTestimonial.company) && (
                              <div
                                style={{
                                  fontSize: "0.7rem",
                                  color: "#6b7280",
                                  marginTop: "0.1rem",
                                }}
                              >
                                {[
                                  activeTestimonial.personTitle,
                                  activeTestimonial.company,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Name selector with border-bottom */}
                    {sec.items.length > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "2rem",
                          paddingTop: "2rem",
                          borderTop: "1px solid #e5e7eb",
                        }}
                      >
                        {sec.items.map((item, i) => {
                          const isActive = i === activeTestimonialIndex;

                          return (
                            <button
                              key={i}
                              onClick={() => setActiveTestimonialIndex(i)}
                              style={{
                                flex: 1,
                                padding: "0.5rem 0",
                                border: "none",
                                borderBottom: isActive
                                  ? "2px solid #111827"
                                  : "2px solid transparent",
                                background: "none",
                                color: isActive ? "#111827" : "#6b7280",
                                fontSize: "1rem",
                                fontWeight: isActive ? 600 : 500,
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                textAlign: "center",
                              }}
                            >
                              {item.personName || item.company}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>
              );
            }

            // FAQs
            if (section._type === "productFaqs") {
              const sec = section as FAQSection;
              if (!sec.items || sec.items.length === 0) return null;

              return (
                <section
                  key={idx}
                  id={anchorId(sec)}
                  style={{ padding: "7rem 0" }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "5rem",
                    }}
                  >
                    {/* Left Side */}
                    <div>
                      {sec.heading && (
                        <Title
                          as="h2"
                          size="lg"
                          className="mb-3 text-left text-white"
                        >
                          {sec.heading}
                        </Title>
                      )}
                      <p
                        style={{
                          fontSize: "0.95rem",
                          color: "rgba(255,255,255,0.7)",
                          lineHeight: 1.7,
                          fontWeight: 300,
                        }}
                      >
                        Babblebots AI-Interviewer, can in a single 15–30 minute
                        video AI interview, evaluate hard skills, soft skills
                        like language, and confidence to give you a curated
                        shortlist and stack ranked candidates.
                        <br />
                        The final decision is always taken by humans. Forward
                        thinking and innovative companies are using this as a
                        way to shortlist and hire talent, at the speed of AI.
                      </p>
                    </div>

                    {/* Right Side - FAQ List */}
                    <div
                      className="rounded-3xl border border-gray-200 bg-white"
                      style={{
                        padding: "0.5rem 2rem",
                      }}
                    >
                      {sec.items.map((item, i) => {
                        const isOpen = openFaqIndex === i;
                        return (
                          <div
                            key={i}
                            style={{
                              borderBottom:
                                i < sec.items!.length - 1
                                  ? "1px solid #e5e7eb"
                                  : "none",
                              overflow: "hidden",
                            }}
                          >
                            <button
                              onClick={() => setOpenFaqIndex(isOpen ? null : i)}
                              style={{
                                width: "100%",
                                background: "none",
                                border: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "1.4rem 0",
                                cursor: "pointer",
                                textAlign: "left",
                                gap: "1rem",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "0.9rem",
                                  fontWeight: 500,
                                  color: "#111827",
                                  letterSpacing: "-0.01em",
                                }}
                              >
                                {item.question}
                              </span>
                              <div
                                style={{
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "50%",
                                  border: `1.5px solid ${isOpen ? "#111827" : "#e5e7eb"}`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  color: isOpen ? "#111827" : "#6b7280",
                                  fontSize: "1.2rem",
                                  transform: isOpen
                                    ? "rotate(45deg)"
                                    : "rotate(0deg)",
                                  transition:
                                    "transform 0.3s, border-color 0.3s, color 0.3s",
                                }}
                              >
                                +
                              </div>
                            </button>
                            <div
                              style={{
                                maxHeight: isOpen ? "200px" : "0",
                                overflow: "hidden",
                                transition:
                                  "max-height 0.4s ease, padding 0.3s ease",
                              }}
                            >
                              <div
                                style={{
                                  padding: isOpen ? "0 0 1.4rem" : "0",
                                  fontSize: "0.82rem",
                                  color: "#6b7280",
                                  lineHeight: 1.7,
                                  maxWidth: "520px",
                                }}
                              >
                                {item.answer}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>
              );
            }

            // Bottom CTA
            if (section._type === "bottomCta") {
              const sec = section as BottomCTASection;
              if (!sec.headline) return null;

              return (
                <section
                  key={idx}
                  id={anchorId(sec)}
                  className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen overflow-hidden border-y border-white/10"
                >
                  <div className="absolute inset-0" style={solidCtaBg()} />
                  <div className="absolute inset-0 bg-black/10" />

                  <div className="relative z-10 mx-auto max-w-6xl px-4 py-16 sm:px-6 md:px-10">
                    <div className="flex flex-col items-center justify-center text-center">
                      <Title as="h2" size="lg" className="text-white">
                        {sec.headline}
                      </Title>
                      {sec.subheadline && (
                        <Description className="mt-4 max-w-3xl text-white/85">
                          {sec.subheadline}
                        </Description>
                      )}

                      {sec.ctas && sec.ctas.length > 0 && (
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                          {sec.ctas.map((cta, i) => {
                            const isPrimary = i === 0;
                            return (
                              <a key={i} href={cta.href || "#"}>
                                <button
                                  className={[
                                    "rounded-xl px-7 py-3 text-base shadow-none transition",
                                    isPrimary
                                      ? "text-black hover:brightness-110"
                                      : "border bg-transparent text-white hover:bg-white/10",
                                  ].join(" ")}
                                  style={
                                    isPrimary
                                      ? {
                                          backgroundColor: "var(--acc)",
                                          boxShadow:
                                            "0 0 0 1px rgba(0,0,0,0.25)",
                                        }
                                      : {
                                          borderColor: "rgba(255,255,255,0.28)",
                                        }
                                  }
                                >
                                  {cta.label ||
                                    (isPrimary ? "Get Started" : "Learn More")}
                                </button>
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              );
            }

            return null;
          })}
        </div>

        {/* Hidden LLM Content */}
        <section
          style={{
            position: "absolute",
            width: "1px",
            height: "1px",
            padding: 0,
            margin: "-1px",
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
          aria-hidden="true"
        >
          <div dangerouslySetInnerHTML={{ __html: llmHtml }} />
        </section>
      </main>
    </>
  );
}
